import { useQuery, useQueryClient } from '@tanstack/react-query'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import {
  DataTable,
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable'
import { IColumnVisibility } from 'components/DataTable/types'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import DistributorAssignModal from 'components/Modals/AssignmentModal/DistributorAssignModal'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import ContactCompanyExportModal from 'components/Modals/ExportModal/ContactCompanyExportModal'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'
import { CompaniesFilterset } from 'components/Tables/CompaniesTable/CompaniesFilterset'
import { getCampaignTableKey, UGC_TABLE_KEY } from 'constants/tableQueryKeys'
import { Category, SubCategory } from 'models/companies'
import React, { useEffect, useMemo, useState } from 'react'
import {
  BsBriefcase,
  BsRocketTakeoff,
  BsTags,
  BsTruck,
  BsXCircle,
} from 'react-icons/bs'
import { PiCalendarStar } from 'react-icons/pi'
import useApiService from 'services/api'
import styled from 'styled-components'
import { FilterIdentifier } from '../../../models/saved_view'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import { CreateDealModalForTable } from '../../Deals/CreateDealModal/CreateDealModal'
import { StandardSummaryResponse } from '../../../models/paginated-response'

import { TableSearch } from '../../Filters/TableSearch'
import * as S from '../CommonTable.styles'
import { AssignToCampaignModal } from '../../../features/campaigns/AssignToCampaignModal'
import { IDropdownItem } from '../../UI/Dropdown/Dropdown'
import { useCampaignUnassigner } from '../../../features/campaigns/useCampaignUnassigner'
import { useFeatureFlagService } from '../../../utils/useFeatureFlagService'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { cn } from '../../UI/cn'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import {
  sortableFieldOptions,
  sortableFields,
  useCompaniesTableCols,
} from './useCompaniesTableColumns'
import TableButtonSort from 'components/Buttons/TableButtons/TableButtonSort'
import GPOAssignModal from 'components/Modals/AssignmentModal/GPOAssignModal'
import { PiShippingContainer } from 'react-icons/pi'
import { ReminderModalForTable } from 'components/Reminders/ReminderModal'
import dialogConfirm from 'components/dialogConfirm'
import { EditCompanyModal } from 'components/Modals/CompanyModal/EditCompanyModal'
import { toast } from 'react-toastify'
import to from 'await-to-js'

const PAGE_SIZE = 100

interface CompaniesTableProps {
  setTotalRowsCount?: (count: StandardSummaryResponse) => void
  pageParamName?: string
  category: Category
  subCategory: SubCategory
  tableKey: string
  filterIdentifierModifier?: FilterIdentifier
  baseFilters?: Record<string, any>
}

function CompaniesTableComponent(props: CompaniesTableProps) {
  const hasCampaign = !!props.baseFilters?.campaigns
  const IDENTIFIER =
    `companies-${props.subCategory?.id}${props.filterIdentifierModifier ?? ''}` as FilterIdentifier
  const companiesFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const { page, setPage } = companiesFilterStore()
  const queryClient = useQueryClient()
  const api = useApiService()
  const featureFlag = useFeatureFlagService()
  const {
    state: { totalSelectedRows, rowSelection },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()
  const { sorting, setSorting } = dataTableSortingStoreRepo.getStore(
    props.tableKey
  )()

  const filterParams = {
    ...useFilterParams(companiesFilterStore),
    ...props.baseFilters,
  }

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}
    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }
    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      sub_category_id: props.subCategory?.id,
      ...filterParams,
      ...sortParams,
    }),
    [filterParams, sortParams]
  )

  const TABLE_QUERY_KEY = [
    hasCampaign
      ? getCampaignTableKey(UGC_TABLE_KEY, props.baseFilters?.campaign)
      : UGC_TABLE_KEY, // Common key for all UGC tables
    ...getTableQueryKey({
      tableKey: props.tableKey,
      filterParams: filterAndSortParams,
      page: page,
    }),
  ]

  const SUMMARY_QUERY_KEY = [...TABLE_QUERY_KEY, 'summary']

  // DATA FETCHING - Main companies list
  const { isFetching, data } = useQuery({
    staleTime: Infinity,
    queryKey: TABLE_QUERY_KEY,
    queryFn: async () => {
      clearSelectedRows()

      const listRes = await api.getCompaniesList(
        props.subCategory?.id as number,
        {
          page: page,
          limit: PAGE_SIZE,
          ...filterAndSortParams,
        }
      )

      return listRes
    },
  })

  // DATA FETCHING - Summary data (separate from main table loading)
  const { data: summaryData } = useQuery({
    staleTime: Infinity,
    queryKey: SUMMARY_QUERY_KEY,
    queryFn: async () => {
      const summaryRes = await api.getCompaniesSummary(
        props.subCategory?.id as number,
        {
          ...filterAndSortParams,
        }
      )

      const finalData = {
        count: summaryRes.count,
        alt_count: summaryRes.alt_count,
      }

      props.setTotalRowsCount?.(finalData)
      setTotalRowsInBackend(summaryRes.count)

      return finalData
    },
  })
  // END DATA FETCHING

  const unassignFromCampaign = useCampaignUnassigner(props.subCategory.slug, {
    data: data?.results ?? [],
    accessorKey: 'id',
    tableQueryKey: TABLE_QUERY_KEY,
    requestParams: filterAndSortParams,
    campaignId: props?.baseFilters?.campaigns,
    setPage: setPage,
    totalCount: summaryData?.count ?? 0,
    pageSize: PAGE_SIZE,
  })

  useEffect(() => {
    clearSelectedRows()
  }, [data])

  const [showAssignTags, setShowAssignTags] = useState(false)
  const [showAssignDistributors, setShowAssignDistributors] = useState(false)
  const [showAssignGPOs, setShowAssignGPOs] = useState(false)
  const [openDealModal, setOpenDealModal] = React.useState(false)
  const [showExportAllModal, setShowExportAllModal] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [companyToEditId, setCompanyToEditId] = useState<number | null>(null)
  const {
    data: companyToEdit,
    isLoading: isLoadingCompanyToEdit,
    isError: isCompanyToEditError,
  } = api.useGetCompany(companyToEditId)

  const handleDeleteCompany = async (companyId: number) => {
    if (
      await dialogConfirm(
        'Are you sure you want to delete this company? This action cannot be undone.',
        'Delete Company'
      )
    ) {
      const [err] = await to(api.deleteCompany(companyId))
      if (err) {
        toast.error('Error deleting company')
        return
      }
      toast.success('Company deleted')
      await queryClient.invalidateQueries({ queryKey: TABLE_QUERY_KEY })
      clearSelectedRows()
    }
  }

  const handleCreateDeals = () => {
    if (!data) return
    setOpenDealModal(true)
  }

  const columns = useCompaniesTableCols({
    sorting,
    inCampaigns: props.baseFilters?.campaigns,
    onDelete: handleDeleteCompany,
    onEdit: (company) => setCompanyToEditId(company.id),
  })

  // Get selected company IDs
  const selectedCompanyIds = useMemo(() => {
    return (
      data?.results
        ?.filter((_, i: number) => rowSelection[i])
        ?.map((r: any) => r.id) ?? []
    )
  }, [data?.results, rowSelection])

  const actions = useMemo(() => {
    const items: IDropdownItem[] = [
      // Only show reminder action when exactly one company is selected
      {
        label: 'Create Deals',
        icon: <BsBriefcase size={20} />,
        callback: handleCreateDeals,
      },
      {
        label: 'Manage Tags',
        icon: <BsTags size={20} />,
        callback: () => setShowAssignTags(true),
      },
      {
        label: 'Add Known Distributors',
        icon: <BsTruck size={20} />,
        callback: () => setShowAssignDistributors(true),
      },
      {
        label: 'Add GPO',
        icon: <PiShippingContainer size={20} />,
        callback: () => setShowAssignGPOs(true),
      },
      ...(featureFlag.enableReminders
        ? [
            {
              label: 'Schedule Reminder',
              icon: <PiCalendarStar size={20} />,
              callback: () => setShowReminderModal(true),
              disabled: selectedCompanyIds.length !== 1,
              disabledTooltipText:
                'Please select exactly one company to schedule a reminder',
            },
          ]
        : []),
    ]

    if (featureFlag.enableCampaigns) {
      if (!hasCampaign) {
        items.push({
          label: 'Push to Campaign',
          icon: <BsRocketTakeoff size={20} />,
          subContent: (
            <AssignToCampaignModal
              data={data?.results ?? []}
              accessorKey={'id'}
              tableQueryKey={TABLE_QUERY_KEY}
              requestParams={filterAndSortParams}
              companyTypeSlug={props.subCategory.slug}
              tableTypeSlug={'companies-table'}
              buttonLess
            />
          ),
        })
      } else {
        items.push({
          label: 'Unassign from Campaign',
          icon: <BsXCircle size={20} />,
          callback: () => unassignFromCampaign.unassign(),
        })
      }
    }

    return items
  }, [data, featureFlag, hasCampaign])

  useEffect(() => {
    if (summaryData) {
      props.setTotalRowsCount?.(summaryData)
      setTotalRowsInBackend?.(summaryData.count)
    }
  }, [summaryData])

  return (
    <TableContainer>
      <DataTable
        tableHeader={
          <S.SearchContainer>
            <div className="flex w-full gap-2 items-center">
              <TableSearch
                className={'flex-1'}
                filterStore={companiesFilterStore}
                searchPlaceholder="Search by name, company..."
                tableKey={props.tableKey}
              />
              <CompaniesFilterset
                filterIdentifier={IDENTIFIER}
                subCategoryId={props.subCategory?.id as number}
                hideCampaignsFilter={props.baseFilters?.campaign}
              />
              <ColumnSelectorRecipient tableKey={props.tableKey} />
              <TableButtonSort
                options={sortableFieldOptions}
                selectedValue={
                  sorting.length
                    ? {
                        value: sorting[0].id,
                        direction: sorting[0].desc ? 'desc' : 'asc',
                      }
                    : undefined
                }
                onSelect={(value, direction) =>
                  setSorting([{ id: value, desc: direction === 'desc' }])
                }
              />
            </div>

            <div className={cn('flex flex-row justify-between gap-4')}>
              <TableButtonAction
                items={actions}
                disabled={!totalSelectedRows}
                selectedRowsCount={totalSelectedRows ?? 0}
              />
              <FilterChips
                identifier={'COMPANIES_TABLE'}
                clearAllButton
                showActive
                store={companiesFilterStore}
              />
            </div>
          </S.SearchContainer>
        }
        tableKey={props.tableKey}
        loading={isFetching || unassignFromCampaign.isUnassigning}
        data={data?.results ?? []}
        columns={columns}
        sortableFields={[...sortableFields]}
        virtualizeRows={false}
        enableRowSelection
        isPaginationEnabled={true}
        paginationOptions={{
          totalRows: summaryData?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        selectAllText={'Select Targets'}
        footerControls={
          <OpportunitiesTableFooterControls
            companyTypeSlug={props.subCategory.slug}
            onDownloadButtonClick={() => setShowExportAllModal(true)}
          />
        }
      />

      <TagAssignModal
        show={showAssignTags}
        handleClose={() => setShowAssignTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) => {
          return api.commonManageTags({
            values,
            tableType: 'companies-table',
          })
        }}
        idAccessor="id"
      />

      <DistributorAssignModal
        show={showAssignDistributors}
        handleClose={() => setShowAssignDistributors(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="id"
        filterAndSortParams={filterAndSortParams}
        apiDistributorsAction={(values) =>
          api.commonManageDistributors({
            tableType: 'companies-table',
            values,
          })
        }
      />

      <GPOAssignModal
        show={showAssignGPOs}
        handleClose={() => setShowAssignGPOs(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        idAccessor="id"
        filterAndSortParams={filterAndSortParams}
        apiGPOsAction={(values) =>
          api.commonManageGPOs({
            tableType: 'companies-table',
            values,
          })
        }
      />

      <ContactCompanyExportModal
        tableTypeSlug={'companies-table'}
        count={summaryData?.count ?? 0}
        show={showExportAllModal}
        _handleClose={() => {
          setShowExportAllModal(false)
        }}
        subCategoryName={props.subCategory?.name as string}
        filters={filterAndSortParams}
      />

      {openDealModal && (
        <CreateDealModalForTable
          optimisticUiTarget="companies"
          show={openDealModal}
          handleClose={() => setOpenDealModal(false)}
          queryKey={TABLE_QUERY_KEY}
          filterAndSortParams={filterAndSortParams}
          data={data?.results ?? []}
          idAccessor="id"
          originCampaignId={props.baseFilters?.campaign}
          tableTypeSlug={'companies-table'}
          companyTypeSlug={props.subCategory.slug}
        />
      )}

      {companyToEditId && (
        <EditCompanyModal
          show={!!companyToEditId}
          loading={isLoadingCompanyToEdit}
          error={isCompanyToEditError}
          setShow={(show) => {
            if (!show) {
              setCompanyToEditId(null)
              queryClient.invalidateQueries({ queryKey: TABLE_QUERY_KEY })
              queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY })
            }
          }}
          company={companyToEdit}
        />
      )}

      {featureFlag.enableReminders && (
        <ReminderModalForTable
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          data={data?.results}
          chainProxyIdAccessorKey="id"
          onCreated={() => {
            setShowReminderModal(false)
          }}
        />
      )}
    </TableContainer>
  )
}

CompaniesTableComponent.displayName = 'CompaniesTable'

export function CompaniesTable(
  props: CompaniesTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      savedPresetsIdentifier={
        `companies-${props.subCategory?.id}` as FilterIdentifier
      }
      defaultColumnVisibility={defaultColumnVisibility}
      preferredGroupOrder={['Target Attributes', 'Campaigns and Deals']}
    >
      <DataTableProvider tableKey={tableKey}>
        <CompaniesTableComponent tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
`
